import React from "react";
import { Switch, cn } from "@ndd/ui";
import { weekRangeLabel, weekdayDateLabel } from "../../../lib/rd-hours-client";
import type { OutsideVisits } from "../../../lib/rd-hours-client";
import type { RdHoursMode } from "../../../lib/rd-hours-draft";
import { hoursFigure } from "../../../lib/rd-hours-editor";
import { CARD, DISPLAY_TITLE } from "./look";

export interface SummaryCardProps {
  mode: RdHoursMode;
  /** One week's Monday; null in Every week. */
  weekStart: string | null;
  /** The Monday a save made now starts from. */
  nextWeekStart: string;
  bookableMinutes: number;
  /** The RD has never saved hours. */
  fresh: boolean;
  /** One week: "Tue, Nov 3" for each day this week replaces. */
  changedLabels: readonly string[];
  /** One week with visits: whether the layer is on; null hides the switch. */
  showVisits: boolean | null;
  onShowVisits: (checked: boolean) => void;
  outside: OutsideVisits;
}

export function summaryTitle(
  mode: RdHoursMode,
  weekStart: string | null,
  bookableMinutes: number
): string {
  const hours = hoursFigure(bookableMinutes);

  return mode === "week" && weekStart !== null
    ? `${hours} bookable hours, ${weekRangeLabel(weekStart, false)}`
    : `${hours} bookable hours a week`;
}

export function summaryNote(props: SummaryCardProps): string {
  if (props.mode === "week") {
    return props.changedLabels.length > 0
      ? `Changed this week only: ${props.changedLabels.join("; ")}.`
      : "Same as your usual week.";
  }

  return props.fresh
    ? "Clients and Always AI can book you only inside these hours."
    : `Breaks not counted. Changes apply from ${weekdayDateLabel(props.nextWeekStart)}.`;
}

function OutsideNote({ outside }: { outside: OutsideVisits }) {
  const word = outside.count === 1 ? "visit is" : "visits are";

  return (
    <div className="flex flex-col gap-1 rounded-lg bg-muted px-3.5 py-3 text-body-lg">
      <span className="font-semibold">
        <span className="text-danger">{outside.count}</span> {word} outside these hours
      </span>
      {outside.lines.map((line) => (
        <span key={line} className="text-muted-strong">
          {line}
        </span>
      ))}
      <span className="text-muted-foreground">
        They stay booked until you move them.
      </span>
    </div>
  );
}

function VisitsToggle({
  props,
  showVisits,
}: {
  props: SummaryCardProps;
  showVisits: boolean;
}) {
  const switchId = React.useId();

  return (
    <div className="flex items-center gap-2.5">
      <Switch id={switchId} checked={showVisits} onCheckedChange={props.onShowVisits} />
      <label htmlFor={switchId} className="cursor-pointer text-body-lg">
        Show booked visits
      </label>
    </div>
  );
}

export function SummaryCard(props: SummaryCardProps) {
  const titleId = React.useId();
  const isWeek = props.mode === "week";

  return (
    <section
      aria-labelledby={titleId}
      className={cn(CARD, "flex flex-col gap-2.5 p-[18px]")}
    >
      <h3 id={titleId} className={DISPLAY_TITLE}>
        {summaryTitle(props.mode, props.weekStart, props.bookableMinutes)}
      </h3>
      <span className="-mt-1.5 text-body-lg text-muted-foreground">
        {summaryNote(props)}
      </span>
      {isWeek && props.showVisits !== null ? (
        <VisitsToggle props={props} showVisits={props.showVisits} />
      ) : null}
      {/* The note stands whether or not the visits are drawn: hiding the layer
          does not move a visit back inside the hours. */}
      {isWeek && props.outside.count > 0 ? (
        <OutsideNote outside={props.outside} />
      ) : null}
    </section>
  );
}
